import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, User, X, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOnClickOutside } from 'usehooks-ts';
import { cn } from '@/shared/utils/utils';
import { UserSelectorAvatar } from './UserSelectorAvatar';
import { UserSelectorEmployee, UserSelectorItem } from './UserSelectorItem';

interface UserSelectorProps {
  title?: string;
  description?: string;
  employees: UserSelectorEmployee[];
  selectedEmployees: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  multiSelect?: boolean;
  customSelectionText?: string;
  className?: string;
  variant?: 'card' | 'inline';
  showClearButton?: boolean;
}

export const UserSelector: React.FC<UserSelectorProps> = ({
  title,
  description,
  employees,
  selectedEmployees,
  onSelectionChange,
  multiSelect = false,
  customSelectionText,
  className = '',
  variant = 'card',
  showClearButton = false,
}) => {
  const { t } = useTranslation('userSelector');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleClickOutside = () => {
    setIsOpen(false);
  };

  useOnClickOutside(
    dropdownRef as React.RefObject<HTMLElement>,
    handleClickOutside
  );

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    } else {
      setSearchTerm('');
    }
  }, [isOpen]);

  const handleEmployeeToggle = (employeeId: string) => {
    const employee = employees.find((emp) => emp.id === employeeId);

    if (!employee?.isActive) return;

    if (multiSelect) {
      const newSelection = selectedEmployees.includes(employeeId)
        ? selectedEmployees.filter((id) => id !== employeeId)
        : [...selectedEmployees, employeeId];
      onSelectionChange(newSelection);
    } else {
      onSelectionChange([employeeId]);
      setIsOpen(false);
    }
  };

  const filteredEmployees = employees.filter((emp) =>
    emp?.fullName?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  const handleSelectAll = () => {
    const enabledEmployeeIds = filteredEmployees
      .filter((emp) => emp.isActive)
      .map((emp) => emp.id);
    onSelectionChange(enabledEmployeeIds);
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectionChange([]);
  };

  const selectedNames = employees
    .filter((emp) => selectedEmployees.includes(emp.id))
    .map((emp) => emp.fullName);

  const selectedEmployee =
    selectedEmployees.length === 1
      ? employees.find((emp) => emp.id === selectedEmployees[0])
      : null;

  const enabledEmployees = filteredEmployees.filter((emp) => emp.isActive);
  const allEnabledSelected =
    enabledEmployees.length > 0 &&
    enabledEmployees.every((emp) => selectedEmployees.includes(emp.id));

  const dropdownContent = (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        type="button"
        className="w-full flex items-center justify-between p-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors min-h-[56px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div className="flex items-center space-x-2 flex-1 h-full">
          {isOpen ? (
            <>
              <Search className="h-4 w-4 text-gray-500" />
              <input
                ref={inputRef}
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="w-full h-full bg-transparent text-sm text-gray-900 focus:outline-none"
              />
            </>
          ) : (
            <>
              {selectedEmployee && (
                <UserSelectorAvatar
                  imageUrl={selectedEmployee.imageUrl}
                  fullName={selectedEmployee.fullName}
                  size="md"
                  className="w-10 h-full"
                />
              )}

              {!selectedEmployee && <User className="h-4 w-4 text-gray-400" />}

              <span className="text-sm text-gray-900">
                {customSelectionText ||
                  (selectedNames.length > 0
                    ? multiSelect && selectedNames.length > 1
                      ? t('selectionCount')(selectedNames.length)
                      : selectedNames[0]
                    : t('selectAssignees'))}
              </span>
            </>
          )}
        </div>

        <div className="flex items-center gap-1">
          {showClearButton && selectedEmployees.length > 0 && !isOpen && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
              type="button"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-gray-400 transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96">
          {filteredEmployees.length === 0 ? (
            <div className="p-3 text-center text-gray-500 text-sm">
              {t('noEmployeesAvailable')}
            </div>
          ) : (
            <>
              {multiSelect && enabledEmployees.length > 0 && (
                <div className="border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between p-3">
                    <span className="text-sm font-medium text-gray-700">
                      {allEnabledSelected
                        ? t('allSelected')
                        : t('selectOptions')}
                    </span>
                    <div className="flex gap-2">
                      {!allEnabledSelected && (
                        <button
                          onClick={handleSelectAll}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {t('selectAll')}
                        </button>
                      )}
                      {selectedEmployees.length > 0 && (
                        <button
                          onClick={handleDeselectAll}
                          className="text-xs text-gray-600 hover:text-gray-800 font-medium"
                        >
                          {t('clearAll')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="max-h-60 overflow-y-auto">
                {filteredEmployees.map((employee) => (
                  <UserSelectorItem
                    key={employee.id}
                    employee={employee}
                    isSelected={selectedEmployees.includes(employee.id)}
                    isMultiSelect={multiSelect}
                    onToggle={handleEmployeeToggle}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  if (variant === 'inline') {
    return <div className={className}>{dropdownContent}</div>;
  }

  return (
    <div
      className={cn(
        'bg-white rounded-lg shadow-sm border border-gray-200 p-4',
        className
      )}
    >
      {title && (
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {description && (
            <p className="text-sm text-gray-600">{description}</p>
          )}
        </div>
      )}

      {dropdownContent}
    </div>
  );
};
